import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomerCampaignService } from '../../../../../../services/customer-campaign.service';
import { catchError, finalize, forkJoin, map, Observable, of, Subject, takeUntil, throwError } from 'rxjs';
import WebsiteUrlStatuses = Boo.Objects.Enums.WebsiteUrlStatuses;
import KeyEnum = Boo.Objects.Enums.KeyEnum;
import CustomValidators from '../../../../../../shared/custom-validators';
import WorkflowHelper from '../../../../../../shared/WorkflowHelper';
import { WorkflowKeyValuePairService } from '../../../../../../services/workflow-keyvaluepair.service';
import { PageOptimization } from '../../../../../../shared/models/page-optimizations/page-optimizations';
import { SaveTypes } from 'app/models/enums/SaveTypes';
import { TaskKeyValuePairService } from '../../../../../../services/task-keyvaluepair.service';
import TaskHelper from 'app/specialist/TaskHelper';
import TaskTypeEnum = Boo.Objects.Enums.TaskTypeEnum;

@Component({
  selector: 'app-components-specialist-actioncomponents-page-optimization-shared-content',
  templateUrl: './content.component.html',
  styleUrls: ['./content.component.scss']
})
export class PageOptimizationContentComponent implements OnInit, OnDestroy {
  @Input() orderingTask: Boo.Objects.Task;
  @Input() workflowId: number;
  @Input() taskId: number;
  @Input() userId: number;
  @Input() customerId: number;
  @Input() websiteUrlId: number;
  @Input() createSnapshot = false;
  @Input() isReadOnly = false;
  @Output() contentChanged = new EventEmitter<void>();

  formGroup: FormGroup;
  additionalKeywordsControl: FormControl<string[]>;
  altImageTagsControl: FormControl<string>;
  bodyCopyControl: FormControl<string>;
  faqCopyControl: FormControl<string>;
  implementationInstructionsControl: FormControl<string>;
  metaDescriptionControl: FormControl<string>;
  schemaMarkupControl: FormControl<string>;
  titleTagControl: FormControl<string>;

  isLoading = true;
  canAddImplementationInstructions = false;
  altKeywords: string[] = [];
  workflowKeyValuePairs: Boo.Objects.WorkflowKeyValuePair[] = [];
  pageOptimization: PageOptimization;

  private destroy$ = new Subject<void>();

  constructor(
    private customerCampaignService: CustomerCampaignService,
    private taskKeyValuePairService: TaskKeyValuePairService,
    private workflowKeyValuePairService: WorkflowKeyValuePairService) { }

  ngOnInit(): void {
    this.canAddImplementationInstructions = this.orderingTask.TaskTypeEnum === TaskTypeEnum.PageOptimization;

    forkJoin([
      this.workflowKeyValuePairService.getByWorkflowId(this.workflowId),
      this.customerCampaignService.getWebsiteUrls(this.customerId, true, [WebsiteUrlStatuses.Target, WebsiteUrlStatuses.Supporting, WebsiteUrlStatuses.New]),
      this.customerCampaignService.getActiveTrackingKeywords(this.customerId)
    ])
    .pipe(
      finalize(() => this.isLoading = false)
    )
    .subscribe({
      next: ([workflowKeyValuePairs, websiteUrls, activeTrackingKeywords]) => {

        this.workflowKeyValuePairs = workflowKeyValuePairs;
        this.pageOptimization = WorkflowHelper.getJSON<PageOptimization>(workflowKeyValuePairs, KeyEnum.PageOptimization) || this.createPageOptimization(this.customerId, this.websiteUrlId);
        this.pageOptimization.updatedInPreview ??= false;

        const otherKeywords = websiteUrls.filter(x => x.WebsiteUrlId !== this.websiteUrlId).flatMap(x => x.Keywords.filter(k => k.IsActive).map(k => k.KeywordPhrase));
        const trackingKeywords = activeTrackingKeywords.map(x => x.KeywordPhrase);

        // if keywords are in this.pageOptimization.additionalKeywords because they have been removed from the campaign, add them to otherKeywords
        this.pageOptimization.additionalKeywords.forEach(keyword => {
          if (!otherKeywords.includes(keyword) && !trackingKeywords.includes(keyword)) {
            otherKeywords.push(keyword);
          }
        });

        this.altKeywords = Array.from(new Set([...otherKeywords, ...trackingKeywords])).sort();
        this.setupForm();
      },
      error: (err) => toastr.error('Failed to load instructions:', err)
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // This form does not have to be valid for SaveTypes.Save
  isValid(saveType: SaveTypes): boolean {
    if (this.isReadOnly) {
      return true;
    }

    this.validateAllControls(false);

    if (!this.formGroup.valid && saveType !== SaveTypes.Save) {
      this.validateAllControls(true);
      return false;
    }

    return true;
  }

  save(saveType: SaveTypes): Observable<void> {
    if (this.isReadOnly || !this.isValid(saveType)) {
      return of(void 0);
    }

    const pageOptimization: PageOptimization = {
      additionalKeywords: this.additionalKeywordsControl.value,
      altImageTags: (this.altImageTagsControl.value ?? '').trim(),
      bodyCopy: (this.bodyCopyControl.value ?? '').trim(),
      customerId: this.customerId,
      faqCopy: (this.faqCopyControl.value ?? '').trim(),
      updatedInPreview: false,
      implementationInstructions: (this.implementationInstructionsControl.value ?? '').trim(),
      metaDescription: (this.metaDescriptionControl.value ?? '').trim(),
      schemaMarkup: (this.schemaMarkupControl.value ?? '').trim(),
      titleTag: (this.titleTagControl.value ?? '').trim(),
      websiteUrlId: this.websiteUrlId
    };

    // Saved consistently to the workflow for deliverable and to the task for historical purposes
    WorkflowHelper.setJSON(this.workflowKeyValuePairs, this.workflowId, KeyEnum.PageOptimization, pageOptimization);
    const saveMethods = [this.workflowKeyValuePairService.save([WorkflowHelper.getWorkflowKeyValuePair(this.workflowKeyValuePairs, KeyEnum.PageOptimization)])];

    if ([SaveTypes.Complete, SaveTypes.Reject].includes(saveType) && this.createSnapshot) {
      const taskKeyValuePairs: Boo.Objects.TaskKeyValuePair[] = [];
      TaskHelper.setJSON(taskKeyValuePairs, this.taskId, this.userId, KeyEnum.PageOptimization, pageOptimization);
      saveMethods.push(this.taskKeyValuePairService.save([TaskHelper.getTaskKeyValuePair(taskKeyValuePairs, KeyEnum.PageOptimization)]));
    }

    return forkJoin(saveMethods)
      .pipe(
        map(() => void 0),
        catchError(err => {
          toastr.error('Failed to save Page Optimization content:', err);
          return throwError(() => err);
        })
      );
  }

  private setupForm(): void {
    this.additionalKeywordsControl = new FormControl(this.pageOptimization.additionalKeywords ?? []);
    this.altImageTagsControl = new FormControl(this.pageOptimization.altImageTags ?? '');
    this.bodyCopyControl = new FormControl(this.pageOptimization.bodyCopy ?? '', [ Validators.required ]);
    this.faqCopyControl = new FormControl(this.pageOptimization.faqCopy ?? '', [ Validators.required ]);
    this.implementationInstructionsControl = new FormControl(this.pageOptimization.implementationInstructions ?? '');
    this.metaDescriptionControl = new FormControl(this.pageOptimization.metaDescription ?? '', [ Validators.required, Validators.maxLength(160) ]);
    this.schemaMarkupControl = new FormControl(this.pageOptimization.schemaMarkup ?? '', [ Validators.required ]);
    this.titleTagControl = new FormControl(this.pageOptimization.titleTag ?? '', [Validators.required, Validators.maxLength(60) ]);
    this.formGroup = new FormGroup({
      additionalKeywords: this.additionalKeywordsControl,
      altImageTags: this.altImageTagsControl,
      bodyCopy: this.bodyCopyControl,
      faqCopy: this.faqCopyControl,
      metaDescription: this.metaDescriptionControl,
      schemaMarkup: this.schemaMarkupControl,
      titleTag: this.titleTagControl
    }, { validators: CustomValidators.minWordCountForControlsValidator(300, ['bodyCopy', 'faqCopy'], 'Body Copy and FAQ Copy must have a combined word count of at least 300') });

    setTimeout(() => {
      this.formGroup.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.contentChanged.emit();
        });
    }, 10);
  }

  private createPageOptimization(customerId: number, websiteUrlId: number): PageOptimization {
    return {
      additionalKeywords: [],
      altImageTags: '',
      bodyCopy: '',
      customerId: customerId,
      faqCopy: '',
      updatedInPreview: false,
      implementationInstructions: '',
      metaDescription: '',
      schemaMarkup: '',
      titleTag: '',
      websiteUrlId: websiteUrlId
    };
  }

  private validateAllControls(emitEvent: boolean) : void {
    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.controls[key].markAsDirty();
      this.formGroup.controls[key].updateValueAndValidity({ emitEvent: emitEvent });
    });
  }
}